<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar">
      <div class="sidebar-header">
        <h2><i class="fas fa-microphone-alt"></i> SpokeT</h2>
      </div>
      
      <nav class="sidebar-nav">
        <a href="#" class="nav-item active" @click.prevent="setActiveTab('overview')">
          <i class="fas fa-home"></i> {{ $t('dashboard.navigation.overview') }}
        </a>
        <a href="#" class="nav-item" @click.prevent="setActiveTab('conversions')">
          <i class="fas fa-file-audio"></i> {{ $t('dashboard.navigation.conversions') }}
        </a>
        <a href="#" class="nav-item" @click.prevent="setActiveTab('statistics')">
          <i class="fas fa-chart-bar"></i> {{ $t('dashboard.navigation.statistics') }}
        </a>
        <a href="#" class="nav-item" @click.prevent="setActiveTab('subscription')">
          <i class="fas fa-credit-card"></i> {{ $t('dashboard.navigation.subscription') }}
        </a>
        <a href="#" class="nav-item" @click.prevent="setActiveTab('settings')">
          <i class="fas fa-cog"></i> {{ $t('dashboard.navigation.settings') }}
        </a>
      </nav>
      
      <div class="sidebar-footer">
        <div class="user-profile">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-info">
            <div class="user-name">{{ userName }}</div>
            <div class="user-plan">{{ $t('dashboard.user.plan', { plan: $t(`plans.${userSubscription}.title`) }) }}</div>
          </div>
          <button class="logout-btn" @click="logout">
            <i class="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </aside>
    
    <!-- Main Content -->
    <main class="main-content">
      <header class="dashboard-header">
        <div class="welcome-message">
          <h1>{{ $t('dashboard.welcome', { name: userName }) }}</h1>
          <p>{{ $t('dashboard.subtitle') }}</p>
        </div>
        
        <div class="quick-actions">
          <button class="action-btn" @click="refreshData">
            <i class="fas fa-sync-alt"></i> {{ $t('dashboard.actions.refresh') }}
          </button>
          <button class="action-btn" @click="exportData">
            <i class="fas fa-download"></i> {{ $t('dashboard.actions.export') }}
          </button>
          <router-link to="/" class="action-btn">
            <i class="fas fa-microphone"></i> {{ $t('dashboard.actions.convert') }}
          </router-link>
        </div>
      </header>
      
      <!-- Overview Tab -->
      <div v-if="activeTab === 'overview'" class="tab-content">
        <!-- Stats Grid -->
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-header">
              <div class="stat-title">{{ $t('dashboard.stats.charactersUsed') }}</div>
              <div class="stat-icon icon-primary">
                <i class="fas fa-font"></i>
              </div>
            </div>
            <div class="stat-value">{{ usage.today.toLocaleString() }}</div>
            <div class="stat-description">{{ $t('dashboard.stats.available', { limit: usage.limit.toLocaleString() }) }}</div>
            <div class="progress-bar">
              <div class="progress-fill progress-primary" :style="{width: usagePercentage + '%'}"></div>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <div class="stat-title">{{ $t('dashboard.stats.conversionsToday') }}</div>
              <div class="stat-icon icon-success">
                <i class="fas fa-wave-square"></i>
              </div>
            </div>
            <div class="stat-value">{{ conversionsToday }}</div>
            <div class="stat-description">{{ $t('dashboard.stats.sinceYesterday', { count: 3 }) }}</div>
            <div class="progress-bar">
              <div class="progress-fill progress-success" style="width: 60%"></div>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <div class="stat-title">{{ $t('dashboard.stats.timeSaved') }}</div>
              <div class="stat-icon icon-warning">
                <i class="fas fa-clock"></i>
              </div>
            </div>
            <div class="stat-value">3.5h</div>
            <div class="stat-description">{{ $t('dashboard.stats.thisWeek') }}</div>
            <div class="progress-bar">
              <div class="progress-fill progress-warning" style="width: 35%"></div>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <div class="stat-title">{{ $t('dashboard.stats.filesSaved') }}</div>
              <div class="stat-icon icon-danger">
                <i class="fas fa-save"></i>
              </div>
            </div>
            <div class="stat-value">127</div>
            <div class="stat-description">{{ $t('dashboard.stats.storage', { size: '5.2GB' }) }}</div>
            <div class="progress-bar">
              <div class="progress-fill progress-danger" style="width: 63.5%"></div>
            </div>
          </div>
        </div>
        
        <!-- Charts Section -->
        <div class="charts-section">
          <div class="chart-card">
            <div class="chart-header">
              <div class="chart-title">{{ $t('dashboard.charts.usageTitle') }}</div>
              <div class="chart-actions">
                <button class="action-btn" :class="{active: chartRange === 'week'}" @click="chartRange = 'week'">{{ $t('dashboard.charts.week') }}</button>
                <button class="action-btn" :class="{active: chartRange === 'month'}" @click="chartRange = 'month'">{{ $t('dashboard.charts.month') }}</button>
              </div>
            </div>
            <div class="chart-container">
              <canvas ref="usageChart"></canvas>
            </div>
          </div>
          
          <div class="chart-card">
            <div class="chart-header">
              <div class="chart-title">{{ $t('dashboard.charts.languageTitle') }}</div>
              <div class="chart-actions">
                <button class="action-btn">{{ $t('dashboard.actions.viewAll') }}</button>
              </div>
            </div>
            <div class="chart-container">
              <canvas ref="languageChart"></canvas>
            </div>
          </div>
        </div>
        
        <!-- Subscription Card -->
        <div class="subscription-card">
          <div class="subscription-header">
            <div class="subscription-title">{{ $t('dashboard.user.plan', { plan: $t(`plans.${userSubscription}.title`) }) }}</div>
            <div class="subscription-badge" :class="userSubscription">{{ subscriptionStatus }}</div>
          </div>
          
          <div class="subscription-features">
            <div class="feature-item" v-for="(feature, index) in subscriptionFeatures" :key="index">
                <i class="fas fa-check-circle"></i>
                <span>{{ feature }}</span>
            </div>
          </div>
          
          <router-link to="/pricing" class="upgrade-btn" v-if="userSubscription === 'free'">
            {{ $t('dashboard.subscription.upgrade') }}
          </router-link>
          <button class="upgrade-btn" v-else>
            {{ $t('dashboard.subscription.manage') }}
          </button>
        </div>
        
        <!-- Recent Activity -->
        <div class="activity-card">
          <div class="chart-header">
            <div class="chart-title">{{ $t('dashboard.activity.title') }}</div>
            <div class="chart-actions">
              <button class="action-btn">{{ $t('dashboard.actions.viewAll') }}</button>
            </div>
          </div>
          
          <ul class="activity-list">
            <li class="activity-item" v-for="activity in recentActivities" :key="activity.id">
              <div class="activity-icon" :class="'icon-' + activity.type">
                <i :class="activity.icon"></i>
              </div>
              <div class="activity-content">
                <div class="activity-title">{{ $t(`dashboard.activity.types.${activity.titleKey}`) }}</div>
                <div class="activity-details">{{ activity.details }}</div>
                <div class="activity-time">{{ activity.time }}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- Other Tabs -->
      <div v-if="activeTab !== 'overview'" class="tab-content">
        <div class="tab-placeholder">
          <i class="fas fa-cogs"></i>
          <h2>{{ $t('dashboard.development.title') }}</h2>
          <p>{{ $t('dashboard.development.message', { section: $t(`dashboard.navigation.${activeTab}`) }) }}</p>
          <button class="back-btn" @click="activeTab = 'overview'">
            {{ $t('dashboard.actions.back') }}
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Chart from 'chart.js/auto';

export default {
  name: 'DashboardPage',
  data() {
    return {
      activeTab: 'overview',
      chartRange: 'week',
      usageChart: null,
      languageChart: null,
      conversionsToday: 18,
      recentActivities: [
        {
          id: 1,
          type: 'primary',
          icon: 'fas fa-file-audio',
          titleKey: 'audioGenerated',
          details: 'Documento_Informe.pdf - 1,245 caracteres',
          time: 'Hace 15 minutos'
        },
        {
          id: 2,
          type: 'success',
          icon: 'fas fa-download',
          titleKey: 'audioDownloaded',
          details: 'Presentacion_Equipo.mp3 - Formato HD',
          time: 'Hace 2 horas'
        },
        {
          id: 3,
          type: 'warning',
          icon: 'fas fa-cog',
          titleKey: 'settingsUpdated',
          details: 'Voz predeterminada cambiada a "Laura (ES)"',
          time: 'Ayer a las 16:30'
        },
        {
          id: 4,
          type: 'danger',
          icon: 'fas fa-exclamation-triangle',
          titleKey: 'limitWarning',
          details: 'Has utilizado el 85% de tus caracteres diarios',
          time: 'Ayer a las 11:45'
        }
      ],
      planFeatures: {
        free: [
          this.$t('dashboard.features.free.0'),
          this.$t('dashboard.features.free.1'),
          this.$t('dashboard.features.free.2'),
          this.$t('dashboard.features.free.3')
        ],
        pro: [
          this.$t('dashboard.features.pro.0'),
          this.$t('dashboard.features.pro.1'),
          this.$t('dashboard.features.pro.2'),
          this.$t('dashboard.features.pro.3')
        ],
        enterprise: [
          this.$t('dashboard.features.enterprise.0'),
          this.$t('dashboard.features.enterprise.1'),
          this.$t('dashboard.features.enterprise.2'),
          this.$t('dashboard.features.enterprise.3'),
          this.$t('dashboard.features.enterprise.4')
        ]
      }
    }
  },
  computed: {
    store() {
      return useStore();
    },
    router() {
      return useRouter();
    },
    user() {
      return this.store.state.user;
    },
    usage() {
      return this.user.usage;
    },
    userSubscription() {
      return this.user.subscription;
    },
    userName() {
      return 'Juan Sánchez';
    },
    userInitials() {
      return this.userName.split(' ').map(n => n[0]).join('');
    },
    usagePercentage() {
      return (this.usage.today / this.usage.limit) * 100;
    },
    subscriptionStatus() {
      return this.userSubscription === 'free' ? 
        this.$t('dashboard.subscription.status.free') : 
        this.$t('dashboard.subscription.status.premium');
    },
    subscriptionFeatures() {
      return this.planFeatures[this.userSubscription] || [];
    }
  },
  mounted() {
    this.initCharts();
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    refreshData() {
      console.log('Actualizando datos...');
    },
    exportData() {
      console.log('Exportando datos...');
    },
    logout() {
      this.store.dispatch('logout');
      this.router.push('/');
    },
    initCharts() {
      // Gráfica de uso semanal
      const usageCtx = this.$refs.usageChart.getContext('2d');
      new Chart(usageCtx, {
        type: 'bar',
        data: {
          labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
          datasets: [{
            label: 'Caracteres Usados',
            data: [3200, 4100, 3850, 5250, 4800, 6250, 4250],
            backgroundColor: '#50C878',
            borderRadius: 5
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#a0a0a0'
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                color: '#a0a0a0'
              }
            }
          }
        }
      });
      
      // Gráfica de distribución por idioma
      const languageCtx = this.$refs.languageChart.getContext('2d');
      new Chart(languageCtx, {
        type: 'doughnut',
        data: {
          labels: ['Español', 'Inglés', 'Francés', 'Alemán', 'Otros'],
          datasets: [{
            data: [65, 15, 10, 5, 5],
            backgroundColor: [
              '#50C878',
              '#03dac6',
              '#cf6679',
              '#ffc107',
              '#4caf50'
            ],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                color: '#a0a0a0',
                padding: 15
              }
            }
          }
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.dashboard-container {
  display: flex;
  min-height: 100vh;
  background-color: $dark-bg-primary;
  font-family: 'Nunito Bold', sans-serif;
  color: #e0e0e0;
}

/* Sidebar Styles */
.sidebar {
  width: 250px;
  background-color: $dark-bg-secondary;
  padding: 20px 0;
  border-right: 1px solid #383838;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 0 20px 20px;
  border-bottom: 1px solid #383838;
  margin-bottom: 20px;
}

.sidebar-header h2 {
  color: $dark-accent-primary;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sidebar-nav {
  flex-grow: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #e0e0e0;
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  cursor: pointer;
}

.nav-item:hover {
  background-color: #2d2d2d;
  color: $dark-accent-primary;
}

.nav-item.active {
  background-color: rgba(187, 134, 252, 0.1);
  border-left-color: $dark-accent-primary;
  color: $dark-accent-primary;
}

.nav-item i {
  margin-right: 10px;
  width: 20px;
  text-align: center;
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid #383838;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, $dark-accent-primary, #03dac6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
}

.user-info {
  flex-grow: 1;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.user-plan {
  font-size: 0.8rem;
  color: #a0a0a0;
}

.logout-btn {
  background: none;
  border: none;
  color: #a0a0a0;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
}

.logout-btn:hover {
  color: $dark-accent-primary;
  background: rgba(187, 134, 252, 0.1);
}

/* Main Content Styles */
.main-content {
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #383838;
}

.welcome-message h1 {
  font-size: 1.8rem;
  margin-bottom: 5px;
}

.welcome-message p {
  color: #a0a0a0;
}

.quick-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: #2d2d2d;
  border: 1px solid #383838;
  color: #e0e0e0;
  padding: 8px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  text-decoration: none;
}

.action-btn:hover {
  background: $dark-accent-primary;
  color: white;
}

.action-btn.active {
  background: $dark-accent-primary;
  color: white;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: $dark-bg-secondary;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border: 1px solid #383838;
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.stat-title {
  color: #a0a0a0;
  font-size: 0.9rem;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.icon-primary {
  background: rgba(187, 134, 252, 0.2);
  color: $dark-accent-primary;
}

.icon-success {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.icon-warning {
  background: rgba(255, 193, 7, 0.2);
  color: #FFC107;
}

.icon-danger {
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.stat-description {
  color: #a0a0a0;
  font-size: 0.9rem;
}

.progress-bar {
  height: 6px;
  background: #2d2d2d;
  border-radius: 3px;
  margin-top: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

.progress-primary {
  background: $dark-accent-primary;
}

.progress-success {
  background: #4CAF50;
}

.progress-warning {
  background: #FFC107;
}

.progress-danger {
  background: #F44336;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-card {
  background: $dark-bg-secondary;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border: 1px solid #383838;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-title {
  font-size: 1.2rem;
  font-weight: 600;
}

.chart-container {
  height: 250px;
  position: relative;
}

/* Subscription Card */
.subscription-card {
  background: linear-gradient(135deg, $dark-accent-primary, #9bfea0);
  border-radius: 12px;
  padding: 25px;
  color: white;
  margin-bottom: 30px;
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.subscription-title {
  font-size: 1.5rem;
  font-weight: 700;
}

.subscription-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: 600;
  backdrop-filter: blur(5px);
}

.subscription-badge.free {
  background: rgba(255, 193, 7, 0.3);
}

.subscription-features {
  margin-bottom: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.upgrade-btn {
  background: white;
  color: $dark-accent-primary;
  border: none;
  padding: 12px 25px;
  border-radius: 50px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  width: 80%;
  text-align: center;
  text-decoration: none;
}

.upgrade-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* Recent Activity */
.activity-card {
  background: $dark-bg-secondary;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border: 1px solid #383838;
  margin-bottom: 30px;
}

.activity-list {
  list-style: none;
}

.activity-item {
  display: flex;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #383838;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.activity-content {
  flex-grow: 1;
}

.activity-title {
  font-weight: 600;
  margin-bottom: 5px;
}

.activity-details {
  color: #a0a0a0;
  font-size: 0.9rem;
}

.activity-time {
  color: #a0a0a0;
  font-size: 0.8rem;
  margin-top: 5px;
}

/* Tab Placeholder */
.tab-placeholder {
  text-align: center;
  padding: 60px 20px;
}

.tab-placeholder i {
  font-size: 3rem;
  color: $dark-accent-primary;
  margin-bottom: 20px;
}

.tab-placeholder h2 {
  margin-bottom: 15px;
  color: $dark-accent-primary;
}

.tab-placeholder p {
  color: #a0a0a0;
  margin-bottom: 30px;
}

.back-btn {
  background: $dark-accent-primary;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #9bfec4;
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 968px) {
  .charts-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    padding: 10px;
  }
   
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .quick-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>